import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Alert,
    Autocomplete,
    TextField
} from '@mui/material'
import {
    Add as AddIcon,
    Edit as EditIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'
import { useNavigate, useParams } from 'react-router-dom'
import { shiftApi } from '../../api/shiftApi'
import { employeeApi } from '../../api/employeeApi'
import toast from 'react-hot-toast'
import { useLoading } from '../../hooks'

const ShiftAssignments = () => {
    const navigate = useNavigate()
    const { id: routeShiftId } = useParams() // Get shift ID from URL
    const { loading, withLoading } = useLoading()
    const [assignments, setAssignments] = useState([])
    const [shifts, setShifts] = useState([])
    const [employees, setEmployees] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false)
    const [selectedAssignment, setSelectedAssignment] = useState(null)
    const [formData, setFormData] = useState({
        employee_id: '',
        shift_id: ''
    })
    const [refreshTrigger, setRefreshTrigger] = useState(0)

    const currentShift = shifts.find(s => s.id === parseInt(routeShiftId))

    // Fetch initial data
    useEffect(() => {
        fetchShifts()
        fetchEmployees()
    }, [])

    // Fetch assignments on page change
    useEffect(() => {
        fetchAssignments()
    }, [page, rowsPerPage, refreshTrigger, routeShiftId]) // Add routeShiftId dependency

    const fetchShifts = async () => {
        try {
            const response = await shiftApi.listShifts()
            setShifts(response.data.shifts || [])
        } catch (error) {
            console.error('Error fetching shifts', error)
            toast.error('Failed to load shifts')
        }
    }

    const fetchEmployees = async () => {
        try {
            const response = await employeeApi.listEmployees()
            const rawEmployees = response.data.employees || []
            const mappedEmployees = rawEmployees.map(emp => ({
                id: emp.employee_id || emp.id,
                name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.name,
                employee_code: emp.employee_code || emp.employeeId
            }))
            setEmployees(mappedEmployees)
        } catch (error) {
            console.error('Error fetching employees', error)
        }
    }

    const fetchAssignments = async () => {
        withLoading(async () => {
            try {
                // If routeShiftId is present, we might want to filter
                // Assuming API supports filtering by shift_id, or we filter client side
                const params = {
                    page: page + 1,
                    limit: rowsPerPage
                }
                if (routeShiftId) {
                    params.shift_id = routeShiftId
                }

                const response = await shiftApi.getAssignments(params)

                let fetchedAssignments = response.data.assignments || []
                let totalCount = response.data.total || 0

                // Fallback client-side filtering if API ignores shift_id param
                if (routeShiftId && !response.config?.params?.shift_id) {
                    // logic if API doesn't support it, but let's assume it does or just display all for now
                    // Actually, better to filter client side if unsure, but user provided curl didn't show filter params
                    // Let's filter client side to be safe if total count is small
                    fetchedAssignments = fetchedAssignments.filter(a => a.shift_id === parseInt(routeShiftId) || a.shift === currentShift?.shift_name)
                    totalCount = fetchedAssignments.length
                }

                setAssignments(fetchedAssignments)
                setTotal(totalCount)
            } catch (error) {
                toast.error('Failed to load assignments')
            }
        })
    }

    const handleOpenDialog = (assignment = null) => {
        if (assignment) {
            setSelectedAssignment(assignment)
            setFormData({
                employee_id: assignment.employee_id || assignment.id,
                shift_id: assignment.shift_id
            })
        } else {
            setSelectedAssignment(null)
            setFormData({
                employee_id: '',
                // If managing a specific shift, pre-fill it
                shift_id: routeShiftId ? parseInt(routeShiftId) : ''
            })
        }
        setOpenDialog(true)
    }

    const handleSubmit = async () => {
        if (!formData.employee_id || !formData.shift_id) {
            toast.error('Please select both employee and shift')
            return
        }

        try {
            if (selectedAssignment) {
                // Update
                await shiftApi.updateAssignment({
                    employee_id: formData.employee_id,
                    shift_id: formData.shift_id
                })
                toast.success('Assignment updated successfully')
            } else {
                // Create
                await shiftApi.assignShift({
                    employee_id: formData.employee_id,
                    shift_id: formData.shift_id
                })
                toast.success('Shift assigned successfully')
            }
            setOpenDialog(false)
            setRefreshTrigger(prev => prev + 1)
        } catch (error) {
            console.error(error)
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton onClick={() => navigate(-1)} size="small">
                                <ArrowBackIcon />
                            </IconButton>
                            <Box>
                                <Typography variant="h4" fontWeight="bold">
                                    {routeShiftId && currentShift
                                        ? `Manage Shift: ${currentShift.shift_name}`
                                        : 'Shift Assignments'}
                                </Typography>
                                {routeShiftId && currentShift && (
                                    <Typography variant="body2" color="textSecondary">
                                        {currentShift.start_time} - {currentShift.end_time}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Assign Employee
                    </Button>
                </Box>

                <Paper variant="outlined" sx={{ width: '100%', mb: 2 }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Current Shift</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableSkeleton columns={4} rows={rowsPerPage} />
                                ) : assignments.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            No assignments found for this shift
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    assignments.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="subtitle2">
                                                        {row.employee_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {row.employee_code}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.shift_name}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{row.department}</TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleOpenDialog(row)}
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10))
                            setPage(0)
                        }}
                    />
                </Paper>

                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {selectedAssignment ? 'Update Shift Assignment' : 'Assign Employee to Shift'}
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                            {/* Employee Select */}
                            {!selectedAssignment ? (
                                <Autocomplete
                                    options={employees}
                                    getOptionLabel={(option) => `${option.name} (${option.employee_code})`}
                                    value={employees.find(e => e.id === formData.employee_id) || null}
                                    onChange={(event, newValue) => {
                                        setFormData({ ...formData, employee_id: newValue ? newValue.id : '' })
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Select Employee" fullWidth />}
                                />
                            ) : (
                                <TextField
                                    label="Employee"
                                    value={selectedAssignment.employee_name}
                                    disabled
                                    fullWidth
                                />
                            )}

                            {/* Shift Select - Disable if managing specifically? No, user might want to move them OUT. But let's pre-select. */}
                            <FormControl fullWidth>
                                <InputLabel>Select Shift</InputLabel>
                                <Select
                                    value={formData.shift_id}
                                    label="Select Shift"
                                    onChange={(e) => setFormData({ ...formData, shift_id: e.target.value })}
                                >
                                    {shifts.map((shift) => (
                                        <MenuItem key={shift.id} value={shift.id}>
                                            {shift.shift_name} ({shift.start_time} - {shift.end_time})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSubmit}>
                            {selectedAssignment ? 'Update' : 'Assign'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Box>
    )
}

export default ShiftAssignments
